import { getAuthToken, getRefreshToken, setAuthToken, setRefreshToken, clearAuth } from "../auth/session";

const BASE = import.meta.env.VITE_API_URL || "/api/v1";

export function toQuery(params: Record<string, any> = {}) {
  const url = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    url.set(k, String(v));
  });
  const s = url.toString();
  return s ? `?${s}` : "";
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // Avoid multiple concurrent refreshes
  if (refreshPromise) return refreshPromise;

  const rt = getRefreshToken();
  if (!rt) return false;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/platform/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return false;
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return false;
      const json = await res.json();
      // Support both wrapped and direct responses
      const data = json?.success && json?.data ? json.data : json;
      if (!data?.accessToken) return false;
      // Update tokens (refresh may be rotated)
      setAuthToken(data.accessToken);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Reset promise after completion
      const p = refreshPromise; // ensure local ref for edge cases
      refreshPromise = null;
      void p; // noop to satisfy linter
    }
  })();

  return refreshPromise;
}

export async function request<T = any>(
  path: string,
  init?: RequestInit & { _retry?: boolean }
): Promise<T> {
  // Get auth token and include in headers if available
  const token = getAuthToken();

  // FormData uploads must NOT carry an explicit Content-Type — the browser
  // sets `multipart/form-data; boundary=…` automatically. Setting JSON here
  // would strip the boundary and break the multipart parser server-side.
  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;

  const headers: Record<string, string> = {};
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Merge with any additional headers from init
  if (init?.headers) {
    Object.assign(headers, init.headers);
  }

  const { _retry, ...fetchInit } = (init || {}) as RequestInit & { _retry?: boolean };

  const res = await fetch(`${BASE}${path}`, {
    ...fetchInit,
    headers,
  });
  if (!res.ok) {
    // Attempt automatic token refresh on unauthorized, once
    if (res.status === 401 && !_retry) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return request<T>(path, { ...(fetchInit as RequestInit), _retry: true });
      }
      // If refresh failed, clear session for a clean state
      clearAuth();
    }
    let errText = `HTTP ${res.status} ${res.url || path}`;
    try {
      const j = await res.json();
      const msg = j?.error?.message || j?.message;
      if (msg) errText = `${errText} - ${msg}`;
    } catch {}
    throw new Error(errText);
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const json = await res.json();
    // Unwrap the response if it has the { success: true, data: {...} } structure
    if (json.success && json.data !== undefined) {
      return json.data as T;
    }
    return json as T;
  }
  return (await res.text()) as unknown as T;
}

/**
 * Download a binary response (e.g. an .xlsx template) directly to disk.
 * `request()` assumes JSON or text; using it for binary would corrupt the
 * bytes. This helper streams the Response into a Blob and triggers a save
 * via a hidden `<a download>` so it works the same in every browser.
 */
export async function downloadBlob(path: string, filename: string): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error?.message || j?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const API_BASE = BASE;
export const api = { toQuery, request };
